import type { RealtimeFanout, RealtimeNotifyEvent } from "@gencow/core";
import { createSingleFlightWake } from "../wake-signal/single-flight-wake.js";
import type { WakeSignalBus } from "../wake-signal/postgres-wake-signal-bus.js";
import {
  LEGACY_REALTIME_OUTBOX_WAKE_CHANNEL,
  buildWakeNotifySql,
  normalizeWakeSignalChannel,
  parseWakeSignalPayload,
} from "../wake-signal/wake-signal-types.js";

type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;
type LoggerLike = Pick<Console, "warn">;

export const DEFAULT_REALTIME_OUTBOX_WAKE_CHANNEL = LEGACY_REALTIME_OUTBOX_WAKE_CHANNEL;
export const REALTIME_OUTBOX_POLL_INTERVAL = 5_000;
export const REALTIME_OUTBOX_MIN_POLL_INTERVAL = 1_000;
export const REALTIME_OUTBOX_MAX_POLL_INTERVAL = 300_000;
export const REALTIME_OUTBOX_POLL_INTERVAL_ENV = "GENCOW_REALTIME_OUTBOX_POLL_INTERVAL_MS";

type OutboxRow = {
  id: unknown;
  event_id: string;
  kind: string;
  channel_key: string | null;
  subscription_key: string | null;
  payload_json: string | null;
};

export type RealtimeOutboxDiagnostics = {
  total: number;
  pending: number;
  retry: number;
  sending: number;
  sent: number;
  dropped: number;
  due: number;
  oldestPendingAgeMs: number;
  oldestPendingCreatedAt: string | null;
};

export type RealtimeOutboxWorker = {
  start(): void;
  stop(): void;
  wake(): Promise<number>;
  tick(): Promise<number>;
};

export type RealtimeOutboxWakeSignal = {
  channel?: string;
};

export type RealtimeOutboxWakeListenerSource = {
  listen(
    channel: string,
    handler: (payload: string) => void | Promise<void>,
  ): (() => void) | Promise<() => void>;
};

const REALTIME_OUTBOX_TABLE_SQL = `
CREATE TABLE IF NOT EXISTS _gencow_realtime_outbox (
  id BIGSERIAL PRIMARY KEY,
  event_id TEXT NOT NULL,
  kind TEXT NOT NULL,
  app_name TEXT NOT NULL,
  channel_key TEXT,
  subscription_key TEXT,
  payload_json TEXT,
  status TEXT NOT NULL DEFAULT 'pending',
  attempt_count INT NOT NULL DEFAULT 0,
  next_attempt_at TIMESTAMP NOT NULL DEFAULT NOW(),
  last_error TEXT,
  created_at TIMESTAMP NOT NULL DEFAULT NOW(),
  sent_at TIMESTAMP
)`;

const REALTIME_OUTBOX_DROP_EVENT_ID_UNIQUE_SQL = `
ALTER TABLE IF EXISTS _gencow_realtime_outbox
  DROP CONSTRAINT IF EXISTS _gencow_realtime_outbox_event_id_key`;

const REALTIME_OUTBOX_DROP_EVENT_ID_UNIQUE_INDEX_SQL = `
DROP INDEX IF EXISTS _gencow_realtime_outbox_event_id_key`;

const REALTIME_OUTBOX_KIND_EVENT_ID_INDEX_SQL = `
CREATE UNIQUE INDEX IF NOT EXISTS idx_gencow_realtime_outbox_kind_event_id
  ON _gencow_realtime_outbox (kind, event_id)`;

const REALTIME_OUTBOX_PENDING_INDEX_SQL = `
CREATE INDEX IF NOT EXISTS idx_gencow_realtime_outbox_pending
  ON _gencow_realtime_outbox (status, next_attempt_at, id)
  WHERE status IN ('pending', 'retry')`;

export const REALTIME_OUTBOX_SYSTEM_SCHEMA_STATEMENTS = [
  REALTIME_OUTBOX_TABLE_SQL,
  REALTIME_OUTBOX_DROP_EVENT_ID_UNIQUE_SQL,
  REALTIME_OUTBOX_DROP_EVENT_ID_UNIQUE_INDEX_SQL,
  REALTIME_OUTBOX_KIND_EVENT_ID_INDEX_SQL,
  REALTIME_OUTBOX_PENDING_INDEX_SQL,
] as const;

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function toCount(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "bigint") return Number(value);
  if (typeof value === "string") return Number(value);
  return 0;
}

function clampRealtimeOutboxPollIntervalMs(value: number): number {
  return Math.min(REALTIME_OUTBOX_MAX_POLL_INTERVAL, Math.max(REALTIME_OUTBOX_MIN_POLL_INTERVAL, value));
}

export function resolveRealtimeOutboxPollIntervalMs(
  env: Record<string, string | undefined> | undefined,
  override?: number,
): number {
  if (typeof override === "number" && Number.isFinite(override) && override > 0) return override;

  const rawValue = env?.[REALTIME_OUTBOX_POLL_INTERVAL_ENV];
  if (!rawValue) return REALTIME_OUTBOX_POLL_INTERVAL;

  const parsedValue = Number(rawValue);
  if (!Number.isInteger(parsedValue) || parsedValue <= 0) return REALTIME_OUTBOX_POLL_INTERVAL;
  return clampRealtimeOutboxPollIntervalMs(parsedValue);
}

function toPgTimestamp(date: Date): string {
  return date.toISOString().replace("T", " ").replace("Z", "");
}

function toIsoString(value: unknown): string | null {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function createEventId(): string {
  const randomUUID = globalThis.crypto?.randomUUID;
  if (typeof randomUUID === "function") return `rt_${randomUUID.call(globalThis.crypto)}`;
  return `rt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

function serializeEvent(appName: string, event: RealtimeNotifyEvent) {
  if (event.type === "emit") {
    return {
      eventId: createEventId(),
      kind: "emit",
      appName,
      subscriptionKey: event.queryKey,
      channelKey: null,
      payloadJson: JSON.stringify({ data: event.data }),
    };
  }
  if (event.type === "invalidate") {
    return {
      eventId: createEventId(),
      kind: "invalidate",
      appName,
      subscriptionKey: null,
      channelKey: null,
      payloadJson: JSON.stringify({ queryKeys: event.queryKeys }),
    };
  }
  if (event.type === "query_invalidate") {
    return {
      eventId: event.eventId,
      kind: "query_invalidate",
      appName,
      subscriptionKey: event.subscriptionKey,
      channelKey: null,
      payloadJson: JSON.stringify({ query: event.query, scopeHash: event.scopeHash, eventId: event.eventId }),
    };
  }
  if (event.type === "query_revoke") {
    return {
      eventId: createEventId(),
      kind: "query_revoke",
      appName,
      subscriptionKey: event.subscriptionKey,
      channelKey: null,
      payloadJson: JSON.stringify({
        query: event.query,
        scopeHash: event.scopeHash,
        epoch: event.epoch,
        reason: event.reason,
      }),
    };
  }
  if (event.type === "channel_publish") {
    return {
      eventId: event.eventId,
      kind: "channel_publish",
      appName,
      subscriptionKey: null,
      channelKey: event.channelKey,
      payloadJson: JSON.stringify({ data: event.data, eventId: event.eventId }),
    };
  }
  if (event.type === "channel_revoke") {
    return {
      eventId: createEventId(),
      kind: "channel_revoke",
      appName,
      subscriptionKey: null,
      channelKey: event.channelKey,
      payloadJson: JSON.stringify({ epoch: event.epoch, reason: event.reason }),
    };
  }
  return {
    eventId: createEventId(),
    kind: "channel_invalidate",
    appName,
    subscriptionKey: null,
    channelKey: event.channelKey,
    payloadJson: null,
  };
}

function parsePayload(row: OutboxRow): Record<string, unknown> {
  if (!row.payload_json) return {};
  const parsed = JSON.parse(row.payload_json);
  return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
}

function rowToEvent(row: OutboxRow): RealtimeNotifyEvent {
  const payload = parsePayload(row);
  if (row.kind === "emit") {
    return { type: "emit", queryKey: row.subscription_key ?? "", data: payload.data };
  }
  if (row.kind === "invalidate") {
    const queryKeys = Array.isArray(payload.queryKeys)
      ? payload.queryKeys.filter((key) => typeof key === "string")
      : [];
    return { type: "invalidate", queryKeys };
  }
  if (row.kind === "query_invalidate") {
    return {
      type: "query_invalidate",
      query: typeof payload.query === "string" ? payload.query : "",
      subscriptionKey: row.subscription_key ?? "",
      scopeHash: typeof payload.scopeHash === "string" ? payload.scopeHash : "",
      eventId: typeof payload.eventId === "string" ? payload.eventId : row.event_id,
    };
  }
  if (row.kind === "query_revoke") {
    return {
      type: "query_revoke",
      query: typeof payload.query === "string" ? payload.query : "",
      subscriptionKey: row.subscription_key ?? "",
      scopeHash: typeof payload.scopeHash === "string" ? payload.scopeHash : "",
      epoch: typeof payload.epoch === "string" ? payload.epoch : undefined,
      reason: typeof payload.reason === "string" ? payload.reason : undefined,
    };
  }
  if (row.kind === "channel_publish") {
    return {
      type: "channel_publish",
      channelKey: row.channel_key ?? "",
      data: payload.data,
      eventId: typeof payload.eventId === "string" ? payload.eventId : row.event_id,
    };
  }
  if (row.kind === "channel_revoke") {
    return {
      type: "channel_revoke",
      channelKey: row.channel_key ?? "",
      epoch: typeof payload.epoch === "string" ? payload.epoch : undefined,
      reason: typeof payload.reason === "string" ? payload.reason : undefined,
    };
  }
  return { type: "channel_invalidate", channelKey: row.channel_key ?? "" };
}

export async function ensureRealtimeOutbox(rawSql: RawSql): Promise<void> {
  if (process.env.GENCOW_METERING_URL) return;
  for (const stmt of REALTIME_OUTBOX_SYSTEM_SCHEMA_STATEMENTS) {
    await rawSql(stmt);
  }
}

export async function notifyRealtimeOutboxWake(params: {
  rawSql: RawSql;
  channel?: string;
}): Promise<void> {
  const statement = buildWakeNotifySql({
    kind: "realtime",
    channel: params.channel,
    defaultChannel: DEFAULT_REALTIME_OUTBOX_WAKE_CHANNEL,
  });
  await params.rawSql(statement.query, statement.params);
}

export async function enqueueRealtimeOutboxEventsInTransaction(params: {
  rawSql: RawSql;
  appName: string;
  events: RealtimeNotifyEvent[];
  wakeSignal?: RealtimeOutboxWakeSignal;
}): Promise<string[]> {
  const eventIds: string[] = [];
  for (const event of params.events) {
    const row = serializeEvent(params.appName, event);
    await params.rawSql(
      `INSERT INTO _gencow_realtime_outbox
         (event_id, kind, app_name, channel_key, subscription_key, payload_json)
       VALUES ($1, $2, $3, $4, $5, $6)
       ON CONFLICT (kind, event_id) DO NOTHING`,
      [row.eventId, row.kind, row.appName, row.channelKey, row.subscriptionKey, row.payloadJson],
    );
    eventIds.push(row.eventId);
  }
  if (eventIds.length > 0 && params.wakeSignal) {
    await notifyRealtimeOutboxWake({
      rawSql: params.rawSql,
      channel: params.wakeSignal.channel,
    });
  }
  return eventIds;
}

export async function enqueueRealtimeOutboxEvent(params: {
  rawSql: RawSql;
  appName: string;
  event: RealtimeNotifyEvent;
}): Promise<string> {
  await ensureRealtimeOutbox(params.rawSql);
  const [eventId] = await enqueueRealtimeOutboxEventsInTransaction({
    rawSql: params.rawSql,
    appName: params.appName,
    events: [params.event],
  });
  return eventId;
}

export async function drainRealtimeOutboxBatch(params: {
  rawSql: RawSql;
  fanout: RealtimeFanout;
  batchSize?: number;
  logger?: LoggerLike;
  isActiveWorker?: () => boolean | Promise<boolean>;
}): Promise<number> {
  await ensureRealtimeOutbox(params.rawSql);
  if (params.isActiveWorker) {
    try {
      if (!(await params.isActiveWorker())) return 0;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      params.logger?.warn(
        `[realtime-outbox] active worker check failed; continuing: ${message.slice(0, 160)}`,
      );
    }
  }
  const batchSize = Math.max(1, Math.min(params.batchSize ?? 100, 500));
  const rows = rowsFromResult<OutboxRow>(
    await params.rawSql(
      `WITH picked AS (
         SELECT id FROM _gencow_realtime_outbox
          WHERE status IN ('pending', 'retry') AND next_attempt_at <= NOW()
          ORDER BY id
          LIMIT $1
          FOR UPDATE SKIP LOCKED
       )
       UPDATE _gencow_realtime_outbox AS outbox
          SET status = 'sending',
              attempt_count = attempt_count + 1
         FROM picked
        WHERE outbox.id = picked.id
        RETURNING outbox.id, outbox.event_id, outbox.kind, outbox.channel_key,
                  outbox.subscription_key, outbox.payload_json`,
      [batchSize],
    ),
  );

  let sent = 0;
  for (const row of rows) {
    try {
      await params.fanout.notify(rowToEvent(row));
      await params.rawSql(
        `UPDATE _gencow_realtime_outbox
            SET status = 'sent', sent_at = NOW(), last_error = NULL
          WHERE id = $1`,
        [row.id],
      );
      sent++;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      params.logger?.warn(`[realtime-outbox] delivery failed: ${message.slice(0, 160)}`);
      await params.rawSql(
        `UPDATE _gencow_realtime_outbox
            SET status = CASE WHEN attempt_count >= 5 THEN 'dropped' ELSE 'retry' END,
                next_attempt_at = NOW() + INTERVAL '5 seconds',
                last_error = $2
          WHERE id = $1`,
        [row.id, message.slice(0, 500)],
      );
    }
  }
  return sent;
}

export async function getRealtimeOutboxDiagnostics(params: {
  rawSql: RawSql;
  now?: Date;
}): Promise<RealtimeOutboxDiagnostics> {
  await ensureRealtimeOutbox(params.rawSql);
  const counts = rowsFromResult<{ status: string; count: unknown }>(
    await params.rawSql(`SELECT status, COUNT(*) AS count FROM _gencow_realtime_outbox GROUP BY status`),
  );
  const stats = {
    pending: 0,
    retry: 0,
    sending: 0,
    sent: 0,
    dropped: 0,
  };
  for (const row of counts) {
    if (row.status in stats) stats[row.status as keyof typeof stats] = toCount(row.count);
  }
  const now = toPgTimestamp(params.now ?? new Date());
  const [lag] = rowsFromResult<{
    due_count: unknown;
    oldest_pending_created_at: unknown;
    oldest_pending_age_ms: unknown;
  }>(
    await params.rawSql(
      `SELECT
         COUNT(*) FILTER (WHERE next_attempt_at <= $1::timestamp) AS due_count,
         MIN(created_at) AS oldest_pending_created_at,
         COALESCE(EXTRACT(EPOCH FROM ($1::timestamp - MIN(created_at))) * 1000, 0) AS oldest_pending_age_ms
       FROM _gencow_realtime_outbox
       WHERE status IN ('pending', 'retry')`,
      [now],
    ),
  );
  return {
    total: stats.pending + stats.retry + stats.sending + stats.sent + stats.dropped,
    ...stats,
    due: toCount(lag?.due_count),
    oldestPendingAgeMs: Math.max(0, Math.floor(toCount(lag?.oldest_pending_age_ms))),
    oldestPendingCreatedAt: toIsoString(lag?.oldest_pending_created_at),
  };
}

export async function cleanupRealtimeOutbox(params: {
  rawSql: RawSql;
  now?: Date;
  sentRetentionMs?: number;
  droppedRetentionMs?: number;
  limit?: number;
}): Promise<number> {
  await ensureRealtimeOutbox(params.rawSql);
  const nowMs = (params.now ?? new Date()).getTime();
  const sentRetentionMs = Math.max(0, params.sentRetentionMs ?? 24 * 60 * 60 * 1000);
  const droppedRetentionMs = Math.max(0, params.droppedRetentionMs ?? 7 * 24 * 60 * 60 * 1000);
  const limit = Math.max(1, Math.min(params.limit ?? 1_000, 10_000));
  const rows = rowsFromResult<{ id: unknown }>(
    await params.rawSql(
      `DELETE FROM _gencow_realtime_outbox
        WHERE id IN (
          SELECT id FROM _gencow_realtime_outbox
           WHERE (status = 'sent' AND COALESCE(sent_at, created_at) < $1::timestamp)
              OR (status = 'dropped' AND created_at < $2::timestamp)
           ORDER BY id
           LIMIT $3
        )
        RETURNING id`,
      [
        toPgTimestamp(new Date(nowMs - sentRetentionMs)),
        toPgTimestamp(new Date(nowMs - droppedRetentionMs)),
        limit,
      ],
    ),
  );
  return rows.length;
}

export function createRealtimeOutboxWorker(params: {
  rawSql: RawSql;
  fanout: RealtimeFanout;
  batchSize?: number;
  intervalMs?: number;
  logger?: LoggerLike;
  isActiveWorker?: () => boolean | Promise<boolean>;
  setIntervalImpl?: typeof setInterval;
  clearIntervalImpl?: typeof clearInterval;
}): RealtimeOutboxWorker {
  const setIntervalImpl = params.setIntervalImpl ?? setInterval;
  const clearIntervalImpl = params.clearIntervalImpl ?? clearInterval;
  const intervalMs = resolveRealtimeOutboxPollIntervalMs(process.env, params.intervalMs);
  let timer: ReturnType<typeof setInterval> | null = null;

  const tick = createSingleFlightWake(() =>
    drainRealtimeOutboxBatch({
      rawSql: params.rawSql,
      fanout: params.fanout,
      batchSize: params.batchSize,
      logger: params.logger,
      isActiveWorker: params.isActiveWorker,
    }),
  );

  return {
    start() {
      if (timer) return;
      timer = setIntervalImpl(() => {
        void tick().catch((error) => {
          const message = error instanceof Error ? error.message : String(error);
          params.logger?.warn(`[realtime-outbox] worker tick failed: ${message.slice(0, 160)}`);
        });
      }, intervalMs);
    },
    stop() {
      if (!timer) return;
      clearIntervalImpl(timer);
      timer = null;
    },
    wake: tick,
    tick,
  };
}

export async function startRealtimeOutboxWakeListener(params: {
  source: RealtimeOutboxWakeListenerSource;
  wakeWorker: () => Promise<unknown>;
  channel?: string;
  appName?: string;
  logger?: LoggerLike;
}): Promise<() => void> {
  const channel = normalizeWakeSignalChannel(params.channel, DEFAULT_REALTIME_OUTBOX_WAKE_CHANNEL);
  return params.source.listen(channel, async (payloadText) => {
    const signal = parseWakeSignalPayload(payloadText);
    if (signal.kind !== "unknown" && signal.kind !== "realtime") return;
    if (params.appName && signal.legacyAppName && signal.legacyAppName !== params.appName) return;
    try {
      await params.wakeWorker();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      params.logger?.warn(`[realtime-outbox] wake signal failed: ${message.slice(0, 160)}`);
    }
  });
}

export async function startRealtimeOutboxWakeBusListener(params: {
  bus: WakeSignalBus;
  wakeWorker: () => Promise<unknown>;
  appName?: string;
  logger?: LoggerLike;
}): Promise<() => Promise<void>> {
  const runStartupScan = async () => {
    await params.wakeWorker().catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      params.logger?.warn(`[realtime-outbox] startup wake scan failed: ${message.slice(0, 160)}`);
    });
  };

  return params.bus.listen(
    ["realtime"],
    async (signal) => {
      if (params.appName && signal.legacyAppName && signal.legacyAppName !== params.appName) return;
      try {
        await params.wakeWorker();
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        params.logger?.warn(`[realtime-outbox] wake signal failed: ${message.slice(0, 160)}`);
      }
    },
    { onListen: runStartupScan },
  );
}

export function createRealtimeOutboxFanout(params: {
  rawSql: RawSql;
  appName: string;
  inner: RealtimeFanout;
  logger?: LoggerLike;
  wakeWorker?: () => Promise<unknown>;
  postgresWakeSignal?: RealtimeOutboxWakeSignal;
}): RealtimeFanout {
  return {
    async notify(event) {
      await ensureRealtimeOutbox(params.rawSql);
      await enqueueRealtimeOutboxEventsInTransaction({
        rawSql: params.rawSql,
        appName: params.appName,
        events: [event],
        wakeSignal: params.postgresWakeSignal,
      });
      if (params.wakeWorker) {
        await params.wakeWorker();
        return;
      }
      await drainRealtimeOutboxBatch({ rawSql: params.rawSql, fanout: params.inner, logger: params.logger });
    },
  };
}
